import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from './api';
import { toast } from 'sonner';
import { User, Mail, Phone, MapPin, Calendar, Edit, Save, X, Users, Building2, Briefcase, CreditCard, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function MyProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  
  const user = JSON.parse(localStorage.getItem('userData') || localStorage.getItem('hrms_user') || '{}');

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/employees/${user.employee_id}`);
      setProfile(response.data);
      setFormData(response.data);
    } catch (error) {
      toast.error('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleSave = async () => {
    try {
      await axios.put(`${API}/me/profile`, formData);
      toast.success('Profile updated successfully');
      setProfile(formData);
      setEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const handleCancel = () => {
    setFormData(profile);
    setEditing(false);
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 dark:border-slate-100"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500 dark:text-slate-400">Profile not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">My Profile</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">Manage your personal information</p>
        </div>
        {!editing ? (
          <Button
            onClick={() => setEditing(true)}
            className="bg-slate-900 hover:bg-slate-800 dark:bg-emerald-600 dark:hover:bg-emerald-700"
            data-testid="edit-profile-btn"
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Profile
          </Button>
        ) : (
          <div className="flex space-x-2">
            <Button
              onClick={handleCancel}
              variant="outline"
              data-testid="cancel-profile-btn"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-profile-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 overflow-hidden">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-8">
          <div className="flex items-center space-x-4">
            <div className="h-20 w-20 rounded-full bg-white flex items-center justify-center">
              <User className="h-10 w-10 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">{profile.full_name}</h3>
              <p className="text-emerald-100">{profile.position || 'Employee'}</p>
              <p className="text-sm text-emerald-50 mt-1">{profile.employee_id}</p>
            </div>
          </div>
        </div>

        {/* Profile Information */}
        <div className="p-8 space-y-8">
          {/* Personal Information */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <User className="h-5 w-5 text-emerald-600" />
              <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Personal Information</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Full Name</Label>
                {editing ? (
                  <Input
                    value={formData.full_name}
                    onChange={(e) => handleInputChange('full_name', e.target.value)}
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.full_name}</p>
                )}
              </div>
              
              <div>
                <Label>Email</Label>
                {editing ? (
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.email}</p>
                )}
              </div>
              
              <div>
                <Label>Phone Number</Label>
                {editing ? (
                  <Input
                    value={formData.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+60 12-345 6789"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.phone || 'Not provided'}</p>
                )}
              </div>
              
              <div>
                <Label>IC Number / Passport</Label>
                {editing ? (
                  <Input
                    value={formData.ic_number || ''}
                    onChange={(e) => handleInputChange('ic_number', e.target.value)}
                    placeholder="123456-78-9012"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.ic_number || 'Not provided'}</p>
                )}
              </div>

              <div>
                <Label>Nationality</Label>
                {editing ? (
                  <Select
                    value={formData.nationality || 'malaysian'}
                    onValueChange={(value) => handleInputChange('nationality', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="malaysian">Malaysian</SelectItem>
                      <SelectItem value="non_malaysian_permanent">Non-Malaysian (Permanent Resident)</SelectItem>
                      <SelectItem value="non_malaysian_temporary">Non-Malaysian (Temporary)</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium capitalize">
                    {profile.nationality?.replace(/_/g, ' ') || 'Malaysian'}
                  </p>
                )}
              </div>

              <div>
                <Label>Marital Status</Label>
                {editing ? (
                  <Select
                    value={formData.marital_status || 'single'}
                    onValueChange={(value) => handleInputChange('marital_status', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="married">Married</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium capitalize">
                    {profile.marital_status || 'Single'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Family & Dependants */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <Users className="h-5 w-5 text-emerald-600" />
              <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Family & Dependants</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Spouse Working?</Label>
                {editing ? (
                  <Select
                    value={formData.spouse_working ? 'yes' : 'no'}
                    onValueChange={(value) => handleInputChange('spouse_working', value === 'yes')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">
                    {profile.spouse_working ? 'Yes' : 'No'}
                  </p>
                )}
              </div>
              
              <div>
                <Label>Number of Children</Label>
                {editing ? (
                  <Input
                    type="number"
                    min="0"
                    value={formData.number_of_children || 0}
                    onChange={(e) => handleInputChange('number_of_children', parseInt(e.target.value) || 0)}
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.number_of_children || 0}</p>
                )}
              </div>

              <div>
                <Label>Disabled Self?</Label>
                {editing ? (
                  <Select
                    value={formData.disabled_self ? 'yes' : 'no'}
                    onValueChange={(value) => handleInputChange('disabled_self', value === 'yes')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">
                    {profile.disabled_self ? 'Yes' : 'No'}
                  </p>
                )}
              </div>

              <div>
                <Label>Disabled Spouse?</Label>
                {editing ? (
                  <Select
                    value={formData.disabled_spouse ? 'yes' : 'no'}
                    onValueChange={(value) => handleInputChange('disabled_spouse', value === 'yes')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">
                    {profile.disabled_spouse ? 'Yes' : 'No'}
                  </p>
                )}
              </div>

              <div>
                <Label>Number of Disabled Children</Label>
                {editing ? (
                  <Input
                    type="number"
                    min="0"
                    value={formData.disabled_children || 0}
                    onChange={(e) => handleInputChange('disabled_children', parseInt(e.target.value) || 0)}
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.disabled_children || 0}</p>
                )}
              </div>
            </div>
          </div>

          {/* Employment Details */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <Briefcase className="h-5 w-5 text-emerald-600" />
              <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Employment Details</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Department</Label>
                <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.department || 'N/A'}</p>
              </div>
              
              <div>
                <Label>Position</Label>
                <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.position || 'N/A'}</p>
              </div>
              
              <div>
                <Label>Join Date</Label>
                <p className="text-slate-900 dark:text-slate-100 font-medium">
                  {new Date(profile.join_date).toLocaleDateString('en-MY', { 
                    day: '2-digit', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </p>
              </div>
              
              <div>
                <Label>Employment Type</Label>
                <p className="text-slate-900 dark:text-slate-100 font-medium capitalize">
                  {profile.employment_type?.replace('_', ' ')}
                </p>
              </div>
              
              {profile.manager_name && (
                <div>
                  <Label>Reporting Manager</Label>
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.manager_name}</p>
                </div>
              )}
            </div>
          </div>

          {/* Banking Information */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <CreditCard className="h-5 w-5 text-emerald-600" />
              <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Banking Information</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Bank Name</Label>
                {editing ? (
                  <Input
                    value={formData.bank_name || ''}
                    onChange={(e) => handleInputChange('bank_name', e.target.value)}
                    placeholder="e.g. Maybank, CIMB, Public Bank"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.bank_name || 'Not provided'}</p>
                )}
              </div>
              
              <div>
                <Label>Bank Account Number</Label>
                {editing ? (
                  <Input
                    value={formData.bank_account || ''}
                    onChange={(e) => handleInputChange('bank_account', e.target.value)}
                    placeholder="Account number"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.bank_account || 'Not provided'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Statutory Information */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <Shield className="h-5 w-5 text-emerald-600" />
              <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Statutory Information</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>EPF Number</Label>
                {editing ? (
                  <Input
                    value={formData.epf_number || ''}
                    onChange={(e) => handleInputChange('epf_number', e.target.value)}
                    placeholder="EPF number"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.epf_number || 'Not provided'}</p>
                )}
              </div>
              
              <div>
                <Label>SOCSO Number</Label>
                {editing ? (
                  <Input
                    value={formData.socso_number || ''}
                    onChange={(e) => handleInputChange('socso_number', e.target.value)}
                    placeholder="SOCSO number"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.socso_number || 'Not provided'}</p>
                )}
              </div>

              <div>
                <Label>EIS Number</Label>
                {editing ? (
                  <Input
                    value={formData.eis_number || ''}
                    onChange={(e) => handleInputChange('eis_number', e.target.value)}
                    placeholder="EIS number"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.eis_number || 'Not provided'}</p>
                )}
              </div>

              <div>
                <Label>Income Tax Number</Label>
                {editing ? (
                  <Input
                    value={formData.tax_number || ''}
                    onChange={(e) => handleInputChange('tax_number', e.target.value)}
                    placeholder="Tax number"
                  />
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{profile.tax_number || 'Not provided'}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
